import React, { useEffect, useState } from 'react';
import { Alert, Dialog, Typography } from '@mui/material';
import { Maximize2, Minimize2, Moon, RefreshCw, Sun } from 'lucide-react';
import Button from '../ActionButton';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useAuth } from '../../../contexts/AuthContext';
import { apiFetch } from '../../../utils/api';
import Chart from '../Reports/components/Chart';
import KPI from '../Reports/components/KPI';
import CalendarPanel from './CalendarPanel';
import { WEEKDAYS, MEALS, ymd, parseDay, shiftDay, dateLabel, monday, sumOrders, weekdayTrends } from './dashboardModel';
import '../Reports/reports.css';
import './dashboard.css';

const theme = createTheme({ typography: { fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' } });

// Reject failed or HTML responses instead of presenting missing data as zero.
async function readJson(path, signal) {
  const response = await apiFetch(path, { signal });
  if (!response.ok) throw new Error(`Dashboard request failed (${response.status}). Please retry.`);
  if (!(response.headers.get('content-type') || '').includes('json')) throw new Error('The dashboard endpoint did not return JSON. Check the deployed backend.');
  return response.json();
}
const requireRows = value => { if (!Array.isArray(value)) throw new Error('Unexpected dashboard data format.'); return value; };

function KitchenTable({ kitchen, onRetry }) {
  if (kitchen.error) return <Alert severity="error">{kitchen.error} <Button variant="text" onClick={onRetry}>Retry</Button></Alert>;
  return <div className="dashboard-kitchen-table-wrap">
    <table className="dashboard-kitchen-table">
      <thead><tr><th scope="col">Kitchen screen</th><th scope="col">Status</th><th scope="col">Pending</th><th scope="col">Watch</th><th scope="col">Overdue</th><th scope="col">Oldest Ticket</th></tr></thead>
      <tbody>
        {kitchen.screens.map(screen => <tr key={screen.station_id}>
          <th scope="row">{screen.station_name || `Screen ${screen.station_id}`}</th>
          <td><span className="dashboard-kitchen-status" data-status={screen.status}><span aria-hidden="true" />{screen.status === 'needs_attention' ? 'Needs attention' : screen.status === 'watch' ? 'Watch' : screen.status === 'idle' ? 'Idle' : 'On pace'}</span></td>
          <td className="dashboard-ticket-count" data-bucket="pending">{Number(screen.pending_count || 0).toLocaleString()}</td>
          <td className="dashboard-ticket-count" data-bucket="watch">{Number(screen.watch_count || 0).toLocaleString()}</td>
          <td className="dashboard-ticket-count" data-bucket="overdue">{Number(screen.overdue_count || 0).toLocaleString()}</td>
          <td>{screen.oldest_ticket_minutes == null ? '—' : `${Number(screen.oldest_ticket_minutes).toFixed(1)} min`}</td>
        </tr>)}
        {!kitchen.loading && !kitchen.screens.length && <tr><td colSpan="6">No configured screens to display.</td></tr>}
      </tbody>
    </table>
    {kitchen.loading && <Typography role="status" variant="body2" color="text.secondary">Loading live kitchen workload…</Typography>}
  </div>;
}

export default function ModernDashboard() {
  const { auth } = useAuth();
  const [today, setToday] = useState(() => ymd(new Date()));
  const [selectedDay, setSelectedDay] = useState('');
  const [month, setMonth] = useState(() => ymd(new Date()).slice(0, 7));
  const [scope, setScope] = useState({ site_id: 'all', location_id: 'all', sublocation_id: 'all' });
  const [hierarchy, setHierarchy] = useState([]);
  const [hierarchyError, setHierarchyError] = useState('');
  const [kitchen, setKitchen] = useState({ loading: true, error: '', screens: [], updated: null });
  const [kitchenExpanded, setKitchenExpanded] = useState(false);
  const [kitchenDark, setKitchenDark] = useState(false);
  const [kitchenRefresh, setKitchenRefresh] = useState(0);
  const [calendar, setCalendar] = useState({ loading: true, error: '', rows: [] });
  const [refresh, setRefresh] = useState(0);
  const [result, setResult] = useState({ loading: true, error: '', data: null, updated: null });
  const defaultStart = shiftDay(today, -29);
  const calendarEarliest = defaultStart;
  const validSelectedDay = selectedDay && selectedDay >= calendarEarliest && selectedDay <= today ? selectedDay : '';
  const endDate = validSelectedDay || today;
  const startDate = validSelectedDay || defaultStart;
  const trendStart = shiftDay(monday(endDate), -49);
  const period = startDate === endDate ? dateLabel(endDate) : `${dateLabel(startDate)} – ${dateLabel(endDate)}`;
  const retry = () => { setToday(ymd(new Date())); setRefresh(value => value + 1); };

  useEffect(() => {
    const controller = new AbortController();
    setHierarchyError('');
    readJson('/dashboard/hierarchy', controller.signal).then(requireRows).then(rows => {
      if (!controller.signal.aborted) setHierarchy(rows);
    }).catch(error => {
      if (!controller.signal.aborted) { setHierarchy([]); setHierarchyError(error.message); }
    });
    return () => controller.abort();
  }, [refresh]);

  // Kitchen workload is live and follows location filters, not the historical calendar selection.
  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams();
    Object.entries(scope).forEach(([key, value]) => { if (value !== 'all') params.set(key, value); });
    const query = params.toString();
    // Never display the previous location's screens under newly selected filters.
    setKitchen({ loading: true, error: '', screens: [], updated: null });
    readJson(`/updates-reports/kitchen-at-a-glance${query ? `?${query}` : ''}`, controller.signal)
      .then(payload => {
        if (!Array.isArray(payload?.screens)) throw new Error('Unexpected kitchen summary format.');
        if (!controller.signal.aborted) setKitchen({ loading: false, error: '', screens: payload.screens, updated: payload.generated_at || null });
      })
      .catch(error => {
        if (!controller.signal.aborted) setKitchen({ loading: false, error: error.message, screens: [], updated: null });
      });
    return () => controller.abort();
  }, [scope, refresh, kitchenRefresh]);

  useEffect(() => {
    // Refresh both views without overlapping slow requests or polling a hidden browser tab.
    if (kitchen.loading) return undefined;
    const interval = window.setInterval(() => {
      if (!document.hidden) setKitchenRefresh(value => value + 1);
    }, 30000);
    return () => window.clearInterval(interval);
  }, [kitchen.loading]);

  // Calendar history is independent so a KPI/chart failure cannot erase its heatmap.
  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams({ startDate: calendarEarliest, endDate: today });
    Object.entries(scope).forEach(([key, value]) => { if (value !== 'all') params.set(key, value); });
    setCalendar(previous => ({ ...previous, loading: true, error: '' }));
    readJson(`/updates-reports/orders-by-day?${params}`, controller.signal).then(requireRows)
      .then(rows => {
        if (!controller.signal.aborted) setCalendar({ loading: false, error: '', rows });
      })
      .catch(error => {
        if (!controller.signal.aborted) setCalendar({ loading: false, error: error.message, rows: [] });
      });
    return () => controller.abort();
  }, [scope, calendarEarliest, today, refresh]);

  // Abort superseded requests so rapid calendar or hierarchy changes cannot show stale results.
  useEffect(() => {
    const controller = new AbortController();
    const query = (start, end, daypart) => {
      const params = new URLSearchParams({ startDate: start, endDate: end });
      Object.entries(scope).forEach(([key, value]) => { if (value !== 'all') params.set(key, value); });
      if (daypart) params.set('daypart', daypart);
      return params.toString();
    };
    const get = (route, start = startDate, end = endDate, daypart) => readJson(`/updates-reports/${route}?${query(start, end, daypart)}`, controller.signal);
    setResult(previous => ({ ...previous, loading: true, error: '', data: null }));
    Promise.all([
      get('orders-by-day').then(requireRows), get('avg-bump-time').then(requireRows), get('total-items-sold'),
      get('orders-by-day', trendStart, endDate).then(requireRows),
      ...MEALS.map(meal => get('orders-by-day', startDate, endDate, meal).then(requireRows)),
    ]).then(([orders, bump, items, history, ...meals]) => {
      if (controller.signal.aborted) return;
      if (items?.total_items_sold == null || !Number.isFinite(Number(items.total_items_sold))) throw new Error('Unexpected item total response.');
      setResult({ loading: false, error: '', updated: new Date(), data: {
        orders: sumOrders(orders), bump: bump[0]?.avg_bump_time_minutes, items: Number(items.total_items_sold),
        trends: weekdayTrends(history, trendStart, endDate),
        meals: MEALS.map((meal, index) => ({ meal, orders: sumOrders(meals[index]) })),
      } });
    }).catch(error => {
      if (!controller.signal.aborted) setResult(previous => ({ ...previous, loading: false, data: null, error: error.message }));
    });
    return () => controller.abort();
  }, [scope, startDate, endDate, trendStart, refresh]);

  const matches = (row, keys) => keys.every(key => scope[key] === 'all' || String(row[key]) === scope[key]);
  const options = (key, name, parents) => [...new Map(hierarchy.filter(row => row[key] != null && matches(row, parents)).map(row => [String(row[key]), row[name] || String(row[key])])).entries()];
  const { data, loading, error, updated } = result;
  const number = value => loading ? '…' : value == null ? '—' : value.toLocaleString();
  const bumpValue = data?.bump == null || !Number.isFinite(Number(data.bump)) ? null : `${Number(data.bump).toFixed(1)} min`;
  const chartState = { loading, error, onRetry: retry, height: 260 };

  return <ThemeProvider theme={theme}>
    <div className="reports-suite dashboard-hub" data-theme="light">
      <header className="dashboard-heading reports-masthead">
        <div className="dashboard-welcome">
          <Typography component="h1" variant="h4">Welcome, {auth?.user?.given_name || auth?.user?.name || auth?.user?.preferred_username || 'User'}</Typography>
          <Typography variant="body3" color="text.secondary" sx={{ mt: 1 }}>Your kitchen at a glance: track orders, monitor performance, and spot daily trends.</Typography>
        </div>
        <div className="dashboard-refresh">
        <Button onClick={retry} disabled={loading}>Refresh Data</Button>
        <Typography variant="caption" color="text.secondary">{updated ? `Last successful refresh: ${updated.toLocaleString()}` : 'Not refreshed yet'}</Typography>
        </div>
      </header>
      <section className="dashboard-toolbar reports-filters" aria-label="Dashboard filters">
        {[
          ['site_id', 'Site', 'site_name', []],
          ['location_id', 'Location', 'location_name', ['site_id']],
          ['sublocation_id', 'Sublocation', 'sublocation_name', ['site_id', 'location_id']],
        ].map(([key, label, name, parents]) => <select key={key} aria-label={label} value={scope[key]} onChange={event => setScope(previous => ({
            ...previous, [key]: event.target.value,
            ...(key === 'site_id' ? { location_id: 'all', sublocation_id: 'all' } : key === 'location_id' ? { sublocation_id: 'all' } : {}),
          }))}>
            <option value="all">All {label.toLowerCase()}s</option>
            {options(key, name, parents).map(([id, text]) => <option key={id} value={id}>{text}</option>)}
          </select>)}
      </section>
      {hierarchyError && <Alert severity="error">{hierarchyError} <Button onClick={retry}>Retry</Button></Alert>}
      {error && <Alert severity="error">{error} <Button onClick={retry}>Retry</Button></Alert>}
      <section aria-label={`Metrics for ${period}`}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{period}</Typography>
        <div className="dashboard-kpis">
          <KPI title="Total Orders" value={number(data?.orders)} color="blue" />
          <KPI title="Avg. Bump Time" value={number(bumpValue)} color="purple" />
          <KPI title="Total Items Sold" value={number(data?.items)} color="green" />
        </div>
      </section>
      <div className="dashboard-columns">
        <CalendarPanel today={today} earliest={calendarEarliest} month={month} onMonthChange={setMonth}
          selectedDay={selectedDay} onSelectDay={setSelectedDay} onClearSelection={() => setSelectedDay('')}
          rows={calendar.rows} loading={calendar.loading} error={calendar.error} onRetry={retry} />
        <section className="dashboard-surface dashboard-kitchen">
          <div className="dashboard-kitchen-heading">
            <Typography variant="subtitle1" component="h2">Kitchen at a Glance</Typography>
            <Button className="dashboard-icon-button" variant="outlined" size="small" onClick={() => setKitchenExpanded(true)} aria-label="Open Kitchen at a Glance full screen" title="Full screen"><Maximize2 size={18} aria-hidden="true" /></Button>
          </div>
          <Typography variant="body2" color="text.secondary">Live workload · current location filters · parked tickets excluded</Typography>
          <KitchenTable kitchen={kitchen} onRetry={() => setKitchenRefresh(value => value + 1)} />
          <Typography variant="caption" color="text.secondary">{kitchen.updated ? `Updated ${new Date(kitchen.updated).toLocaleString()}. ` : ''}Status uses each screen’s configured warning and overdue thresholds.</Typography>
        </section>
      </div>
      <div className="dashboard-columns">
        <Chart {...chartState} type="bar" title="Performance by Meal Period" subtitle={period}
          data={data?.meals || []} showLegend={false}
          xAxis={[{ dataKey: 'meal', scaleType: 'band', colorMap: { type: 'ordinal', values: MEALS, colors: ['#f59e0b', '#10b981', '#2563eb', '#8b5cf6'] } }]}
          yAxis={[{ min: 0, tickMinStep: 1 }]}
          series={[{ dataKey: 'orders', label: 'Orders', valueFormatter: value => `${value ?? 0} orders` }]}
          />
        <Chart {...chartState} type="line" title="Day-of-Week Trend Lines"
          subtitle={`Eight weeks through ${dateLabel(endDate)} · each line tracks one weekday`}
          data={data?.trends || []} xAxis={[{ dataKey: 'week_start', scaleType: 'point', valueFormatter: (value, context) => context.location === 'tick' ? parseDay(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : `Week of ${dateLabel(value)}` }]}
          yAxis={[{ min: 0, tickMinStep: 1 }]}
          series={WEEKDAYS.map(day => ({ dataKey: day, label: day, showMark: false, curve: 'monotoneX', connectNulls: false }))}
        />
      </div>
      <Dialog fullScreen open={kitchenExpanded} onClose={() => setKitchenExpanded(false)}>
        <div className="reports-suite dashboard-hub dashboard-kitchen-fullscreen" data-theme={kitchenDark ? 'dark' : 'light'}>
          <header className="dashboard-kitchen-fullscreen-header">
            <div>
              <Typography component="h2" variant="h4">Kitchen at a Glance</Typography>
              <Typography variant="body2">Live workload · refreshes every 30 seconds · parked tickets excluded</Typography>
            </div>
            <div className="dashboard-kitchen-fullscreen-actions">
              <Button className="dashboard-icon-button" variant="outlined" onClick={() => setKitchenDark(value => !value)} aria-label={kitchenDark ? 'Use light mode' : 'Use dark mode'} title={kitchenDark ? 'Light mode' : 'Dark mode'} aria-pressed={kitchenDark}>
                {kitchenDark ? <Sun size={20} aria-hidden="true" /> : <Moon size={20} aria-hidden="true" />}
              </Button>
              <Button className="dashboard-icon-button" variant="outlined" onClick={() => setKitchenRefresh(value => value + 1)} disabled={kitchen.loading} aria-label="Refresh kitchen workload" title="Refresh"><RefreshCw size={20} aria-hidden="true" /></Button>
              <Button className="dashboard-icon-button" onClick={() => setKitchenExpanded(false)} aria-label="Exit Kitchen at a Glance full screen" title="Exit full screen"><Minimize2 size={20} aria-hidden="true" /></Button>
            </div>
          </header>
          <section className="dashboard-surface dashboard-kitchen" aria-label="Full-screen kitchen workload">
            <KitchenTable kitchen={kitchen} onRetry={() => setKitchenRefresh(value => value + 1)} />
            <Typography variant="caption">{kitchen.updated ? `Updated ${new Date(kitchen.updated).toLocaleString()}. ` : ''}</Typography>
          </section>
        </div>
      </Dialog>
    </div>
  </ThemeProvider>;
}
